import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AppBar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import Swal from "sweetalert2";
import MainContentContainer from "../../MainContentContainer/MainContentContainer";
import LoadingBackdrop from "../../Common/LoadingBackdrop";
import {
  deleteTranslationGlossaryTerm,
  deleteTranslationInstruction,
  fetchTranslationGlossary,
  fetchTranslationInstructions,
  saveTranslationGlossaryTerm,
  saveTranslationInstruction,
} from "../../../actions/captionsActions";

// v1 ships Spanish only. The backend schema + prompt builder are
// language-agnostic; expand this list when Kelly + Nick green-light
// shipping additional languages.
const SUPPORTED_LANGUAGES = [{ value: "es", label: "Spanish (es)" }];

const GLOSSARY_CATEGORIES = [
  { value: "tapping_point", label: "Tapping point" },
  { value: "setup_phrase", label: "Setup phrase" },
  { value: "technical", label: "Technical" },
  { value: "brand", label: "Brand" },
];

const INSTRUCTION_CATEGORIES = [
  { value: "tone", label: "Tone" },
  { value: "brand_protection", label: "Brand protection" },
  { value: "style", label: "Style" },
  { value: "formality", label: "Formality" },
];

const GLOSSARY_CATEGORY_COLOR = {
  tapping_point: "primary",
  setup_phrase: "secondary",
  technical: "info",
  brand: "warning",
};

const INSTRUCTION_CATEGORY_COLOR = {
  tone: "info",
  brand_protection: "warning",
  style: "primary",
  formality: "secondary",
};

const blankGlossary = languageCode => ({
  language_code: languageCode,
  source_term: "",
  target_term: "",
  category: "technical",
  notes: "",
  is_active: true,
});

const blankInstruction = languageCode => ({
  language_code: languageCode,
  instruction: "",
  category: "tone",
  sort_order: 100,
  is_active: true,
});

const CaptionsTranslationPage = () => {
  const dispatch = useDispatch();
  const glossary = useSelector(s => s.captions.translationGlossary);
  const instructions = useSelector(s => s.captions.translationInstructions);
  const isLoading = glossary === null || instructions === null;

  const [language, setLanguage] = useState(SUPPORTED_LANGUAGES[0].value);

  const [glossaryDialogOpen, setGlossaryDialogOpen] = useState(false);
  const [glossaryDraft, setGlossaryDraft] = useState(() => blankGlossary(SUPPORTED_LANGUAGES[0].value));
  const [glossarySubmitting, setGlossarySubmitting] = useState(false);

  const [instructionDialogOpen, setInstructionDialogOpen] = useState(false);
  const [instructionDraft, setInstructionDraft] = useState(() => blankInstruction(SUPPORTED_LANGUAGES[0].value));
  const [instructionSubmitting, setInstructionSubmitting] = useState(false);

  // Per-id in-flight set for inline toggles — same pattern as the
  // Vocabulary page; prevents rapid clicks from racing N PUTs.
  const [togglingGlossaryIds, setTogglingGlossaryIds] = useState(() => new Set());
  const [togglingInstructionIds, setTogglingInstructionIds] = useState(() => new Set());

  useEffect(() => {
    dispatch(fetchTranslationGlossary({ language }));
    dispatch(fetchTranslationInstructions({ language }));
  }, [dispatch, language]);

  // Client-side sort matches the backend index order (glossary: category
  // then source_term; instructions: sort_order then id). Sorting here as
  // well — not just relying on backend order — because UPSERT actions
  // append newly-added rows at the end of the array, so a newly-added
  // instruction with sort_order=1 would otherwise land at the bottom of
  // the table instead of slotting into its priority position.
  const glossaryRows = useMemo(() => {
    if (!Array.isArray(glossary)) return [];
    return glossary
      .filter(g => g.language_code === language)
      .slice()
      .sort((a, b) => {
        const cat = String(a.category ?? "").localeCompare(String(b.category ?? ""));
        if (cat !== 0) return cat;
        return String(a.source_term ?? "").localeCompare(String(b.source_term ?? ""));
      });
  }, [glossary, language]);

  const instructionRows = useMemo(() => {
    if (!Array.isArray(instructions)) return [];
    return instructions
      .filter(i => i.language_code === language)
      .slice()
      .sort((a, b) => {
        const orderDiff = (a.sort_order ?? 0) - (b.sort_order ?? 0);
        if (orderDiff !== 0) return orderDiff;
        return (a.id ?? 0) - (b.id ?? 0);
      });
  }, [instructions, language]);

  // --- Glossary handlers ---
  const openCreateGlossary = () => {
    setGlossaryDraft(blankGlossary(language));
    setGlossaryDialogOpen(true);
  };
  const openEditGlossary = term => {
    setGlossaryDraft({ ...term });
    setGlossaryDialogOpen(true);
  };
  const closeGlossaryDialog = () => setGlossaryDialogOpen(false);

  const onSaveGlossary = () => {
    if (glossarySubmitting) return;
    if (!glossaryDraft.source_term.trim() || !glossaryDraft.target_term.trim()) {
      Swal.fire({ icon: "warning", title: "Both source and target are required.", timer: 1500 });
      return;
    }
    const payload = {
      ...glossaryDraft,
      source_term: glossaryDraft.source_term.trim(),
      target_term: glossaryDraft.target_term.trim(),
      notes: glossaryDraft.notes?.trim() || null,
    };
    setGlossarySubmitting(true);
    dispatch(saveTranslationGlossaryTerm(payload, () => setGlossaryDialogOpen(false))).finally(() =>
      setGlossarySubmitting(false)
    );
  };

  const onToggleGlossaryActive = term => {
    if (togglingGlossaryIds.has(term.id)) return;
    setTogglingGlossaryIds(prev => {
      const next = new Set(prev);
      next.add(term.id);
      return next;
    });
    dispatch(saveTranslationGlossaryTerm({ ...term, is_active: !term.is_active }, null, { silent: true }))
      .catch(() => {})
      .finally(() => {
        setTogglingGlossaryIds(prev => {
          const next = new Set(prev);
          next.delete(term.id);
          return next;
        });
      });
  };

  const onDeleteGlossary = async term => {
    const { isConfirmed } = await Swal.fire({
      icon: "question",
      title: term.is_active ? "Deactivate this entry?" : "Permanently delete this entry?",
      text: term.is_active
        ? "The entry will be removed from the translator's prompt but kept on file for re-activation."
        : "This entry is already inactive. Permanently deleting it removes the row — there's no undo.",
      showCancelButton: true,
      confirmButtonText: term.is_active ? "Deactivate" : "Delete forever",
      confirmButtonColor: term.is_active ? undefined : "#d33",
    });
    if (!isConfirmed) return;
    dispatch(deleteTranslationGlossaryTerm(term.id, null, { force: !term.is_active }));
  };

  // --- Instruction handlers ---
  const openCreateInstruction = () => {
    setInstructionDraft(blankInstruction(language));
    setInstructionDialogOpen(true);
  };
  const openEditInstruction = inst => {
    setInstructionDraft({ ...inst });
    setInstructionDialogOpen(true);
  };
  const closeInstructionDialog = () => setInstructionDialogOpen(false);

  const onSaveInstruction = () => {
    if (instructionSubmitting) return;
    if (!instructionDraft.instruction.trim()) {
      Swal.fire({ icon: "warning", title: "Instruction text is required.", timer: 1500 });
      return;
    }
    const payload = {
      ...instructionDraft,
      instruction: instructionDraft.instruction.trim(),
      sort_order: Number.isFinite(Number(instructionDraft.sort_order)) ? Number(instructionDraft.sort_order) : 0,
    };
    setInstructionSubmitting(true);
    dispatch(saveTranslationInstruction(payload, () => setInstructionDialogOpen(false))).finally(() =>
      setInstructionSubmitting(false)
    );
  };

  const onToggleInstructionActive = inst => {
    if (togglingInstructionIds.has(inst.id)) return;
    setTogglingInstructionIds(prev => {
      const next = new Set(prev);
      next.add(inst.id);
      return next;
    });
    dispatch(saveTranslationInstruction({ ...inst, is_active: !inst.is_active }, null, { silent: true }))
      .catch(() => {})
      .finally(() => {
        setTogglingInstructionIds(prev => {
          const next = new Set(prev);
          next.delete(inst.id);
          return next;
        });
      });
  };

  const onDeleteInstruction = async inst => {
    const { isConfirmed } = await Swal.fire({
      icon: "question",
      title: inst.is_active ? "Deactivate this instruction?" : "Permanently delete this instruction?",
      text: inst.is_active
        ? "The instruction will be removed from the translator's prompt but kept on file."
        : "This instruction is already inactive. Permanently deleting removes the row — no undo.",
      showCancelButton: true,
      confirmButtonText: inst.is_active ? "Deactivate" : "Delete forever",
      confirmButtonColor: inst.is_active ? undefined : "#d33",
    });
    if (!isConfirmed) return;
    dispatch(deleteTranslationInstruction(inst.id, null, { force: !inst.is_active }));
  };

  if (isLoading) {
    return <LoadingBackdrop open />;
  }

  return (
    <MainContentContainer>
      <AppBar position="static" color="inherit" elevation={0} sx={{ borderBottom: "1px solid #e0e0e0", mb: 2 }}>
        <Toolbar disableGutters sx={{ px: 2, gap: 2 }}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            Caption translation
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="lang-label">Language</InputLabel>
            <Select
              labelId="lang-label"
              value={language}
              label="Language"
              onChange={e => setLanguage(e.target.value)}
            >
              {SUPPORTED_LANGUAGES.map(l => (
                <MenuItem key={l.value} value={l.value}>
                  {l.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Toolbar>
      </AppBar>

      <Box sx={{ px: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Manage the glossary and style/tone instructions used when translating approved English captions into the
          selected language. Changes take effect on the next translation job — existing translated captions are not
          re-translated automatically; click <strong>Regenerate</strong> on a session to apply.
        </Typography>

        {/* ============ Glossary table ============ */}
        <Paper sx={{ mb: 4 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ p: 2, backgroundColor: "grey.100" }}
          >
            <Box>
              <Typography variant="h6">Glossary</Typography>
              <Typography variant="caption" color="text.secondary">
                Source-language phrases that should always render as a specific target-language phrase. Brand and
                tapping-point names live here.
              </Typography>
            </Box>
            <Button startIcon={<Add />} variant="contained" size="small" onClick={openCreateGlossary}>
              Add entry
            </Button>
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Source (EN)</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Target ({language.toUpperCase()})</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Category</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Notes</strong>
                  </TableCell>
                  <TableCell align="center" sx={{ width: 80 }}>
                    <strong>Active</strong>
                  </TableCell>
                  <TableCell align="right" sx={{ width: 96 }}>
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {glossaryRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No glossary entries for this language yet.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  glossaryRows.map(term => (
                    <TableRow key={term.id} hover sx={{ opacity: term.is_active ? 1 : 0.55 }}>
                      <TableCell>{term.source_term}</TableCell>
                      <TableCell>{term.target_term}</TableCell>
                      <TableCell>
                        <Chip
                          label={(term.category || "").replace(/_/g, " ")}
                          color={GLOSSARY_CATEGORY_COLOR[term.category] || "default"}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 240 }}>
                        <Typography variant="body2" color="text.secondary" noWrap title={term.notes ?? ""}>
                          {term.notes || ""}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={!!term.is_active}
                          onChange={() => onToggleGlossaryActive(term)}
                          size="small"
                          disabled={togglingGlossaryIds.has(term.id)}
                          inputProps={{ "aria-label": `Toggle active for ${term.source_term}` }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => openEditGlossary(term)}
                            aria-label={`Edit ${term.source_term}`}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={term.is_active ? "Deactivate" : "Delete permanently"}>
                          <IconButton
                            size="small"
                            onClick={() => onDeleteGlossary(term)}
                            color={term.is_active ? "default" : "error"}
                            aria-label={`${term.is_active ? "Deactivate" : "Delete"} ${term.source_term}`}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* ============ Instructions table ============ */}
        <Paper sx={{ mb: 4 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ p: 2, backgroundColor: "grey.100" }}
          >
            <Box>
              <Typography variant="h6">Style &amp; tone instructions</Typography>
              <Typography variant="caption" color="text.secondary">
                Natural-language rules appended to the translator's system prompt. Lower sort order = higher priority
                in the prompt.
              </Typography>
            </Box>
            <Button startIcon={<Add />} variant="contained" size="small" onClick={openCreateInstruction}>
              Add instruction
            </Button>
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 64 }}>
                    <strong>Order</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Instruction</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Category</strong>
                  </TableCell>
                  <TableCell align="center" sx={{ width: 80 }}>
                    <strong>Active</strong>
                  </TableCell>
                  <TableCell align="right" sx={{ width: 96 }}>
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {instructionRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No instructions for this language yet.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  instructionRows.map(inst => (
                    <TableRow key={inst.id} hover sx={{ opacity: inst.is_active ? 1 : 0.55 }}>
                      <TableCell>{inst.sort_order}</TableCell>
                      <TableCell sx={{ maxWidth: 480 }}>
                        <Typography variant="body2">{inst.instruction}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={(inst.category || "").replace(/_/g, " ")}
                          color={INSTRUCTION_CATEGORY_COLOR[inst.category] || "default"}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={!!inst.is_active}
                          onChange={() => onToggleInstructionActive(inst)}
                          size="small"
                          disabled={togglingInstructionIds.has(inst.id)}
                          inputProps={{ "aria-label": `Toggle active for instruction ${inst.id}` }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => openEditInstruction(inst)}
                            aria-label={`Edit instruction ${inst.id}`}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={inst.is_active ? "Deactivate" : "Delete permanently"}>
                          <IconButton
                            size="small"
                            onClick={() => onDeleteInstruction(inst)}
                            color={inst.is_active ? "default" : "error"}
                            aria-label={`${inst.is_active ? "Deactivate" : "Delete"} instruction ${inst.id}`}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* ============ Glossary dialog ============ */}
      <Dialog open={glossaryDialogOpen} onClose={closeGlossaryDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{glossaryDraft.id ? "Edit glossary entry" : "Add glossary entry"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Source term (English)"
              value={glossaryDraft.source_term}
              onChange={e => setGlossaryDraft({ ...glossaryDraft, source_term: e.target.value })}
              fullWidth
              required
              autoFocus={!glossaryDraft.id}
            />
            <TextField
              label={`Target term (${language.toUpperCase()})`}
              value={glossaryDraft.target_term}
              onChange={e => setGlossaryDraft({ ...glossaryDraft, target_term: e.target.value })}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel id="glossary-cat-label">Category</InputLabel>
              <Select
                labelId="glossary-cat-label"
                value={glossaryDraft.category}
                label="Category"
                onChange={e => setGlossaryDraft({ ...glossaryDraft, category: e.target.value })}
              >
                {GLOSSARY_CATEGORIES.map(c => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Notes"
              value={glossaryDraft.notes ?? ""}
              onChange={e => setGlossaryDraft({ ...glossaryDraft, notes: e.target.value })}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeGlossaryDialog} disabled={glossarySubmitting}>
            Cancel
          </Button>
          <Button onClick={onSaveGlossary} variant="contained" disabled={glossarySubmitting}>
            {glossaryDraft.id ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============ Instruction dialog ============ */}
      <Dialog open={instructionDialogOpen} onClose={closeInstructionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{instructionDraft.id ? "Edit instruction" : "Add instruction"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Instruction"
              value={instructionDraft.instruction}
              onChange={e => setInstructionDraft({ ...instructionDraft, instruction: e.target.value })}
              fullWidth
              required
              multiline
              minRows={2}
              autoFocus={!instructionDraft.id}
              helperText="One natural-language rule. Gets appended to the translator's system prompt."
            />
            <FormControl fullWidth>
              <InputLabel id="instr-cat-label">Category</InputLabel>
              <Select
                labelId="instr-cat-label"
                value={instructionDraft.category}
                label="Category"
                onChange={e => setInstructionDraft({ ...instructionDraft, category: e.target.value })}
              >
                {INSTRUCTION_CATEGORIES.map(c => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Sort order"
              type="number"
              value={instructionDraft.sort_order}
              onChange={e => setInstructionDraft({ ...instructionDraft, sort_order: e.target.value })}
              fullWidth
              helperText="Lower = higher priority in the prompt. Default 100."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeInstructionDialog} disabled={instructionSubmitting}>
            Cancel
          </Button>
          <Button onClick={onSaveInstruction} variant="contained" disabled={instructionSubmitting}>
            {instructionDraft.id ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContentContainer>
  );
};

export default CaptionsTranslationPage;
